<template>
    <div class="shipments-table">
        <div class="shipments-table__table">
            <v-data-table
                :headers="headers"
                :items="filteredShipments"
                :items-per-page="-1"
                :footer-props="{
                    'items-per-page-text': $t('pages.shipments.rowsPerPage'),
                }"
                :no-data-text="$t('pages.shipments.noDataAvailable')"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :loading="isFetching"
                dense
                hide-default-footer
                multi-sort
                fixed-header
                @update:sort-by="updateSortBy"
                @update:sort-desc="updateSortDesc"
                @click:row="openShipment"
            >
                <!--    eslint-disable-next-line -->
                <template #item.created_at="{ item }">
                    <span class="nowrap-cell" style="direction: ltr; display: inline-block">
                        <DateCell :value="item.created_at" :utc="false" />
                    </span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.senders="{ item }">
                    <span class="nowrap-cell-tablet">{{ formatSenders(item.senders) }}</span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.recipient="{ item }">
                    <span class="nowrap-cell-tablet">{{ getRecipientName(item) }}</span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.created_by="{ item }">
                    {{ item.createdBy?.nickName || '-' }}
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.total_price="{ item }">
                    <span class="nowrap-cell">{{ formatPrice(item.sum || 0, item.currency) }}</span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.paid="{ item }">
                    <span class="nowrap-cell">{{ formatPrice(item.paid_sum || 0, item.currency) }}</span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.outstanding="{ item }">
                    <span
                        class="nowrap-cell"
                        :class="{ 'shipments-table__debt--unpaid': getOutstanding(item) > 0 }"
                    >{{ formatPrice(getOutstanding(item), item.currency) }}</span>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.status="{ item }">
                    <v-chip
                        small
                        :color="getStatusColor(item.status)"
                        text-color="white"
                    >
                        {{ getStatusText(item.status) }}
                    </v-chip>
                </template>

                <!--    eslint-disable-next-line -->
                <template #item.document_link="{ item }">
                    <ShipmentDocumentLink
                        :link="item.document_link"
                        button-class="shipments-table__document-link"
                        icon-class="bi bi-file-earmark-pdf shipments-table__document-icon"
                    />
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import { mapGetters } from 'vuex'
import { getterTypes } from '@/store/modules/shipments'
import DateCell from '@/components/common/cells/date-cell.vue'
import ShipmentDocumentLink from '@/components/shipments/shipment-document-link.vue'
import prepareSortOptionsReports from '@/helpers/prepare-sort-options-reports.utils'
import arraysEqual from '@/helpers/arrays-equal.helper'
import formatSenders from '@/helpers/format-senders.utils'
import { formatPriceWithCurrency } from '@/helpers/format-price-with-currency'

export default {
    name: 'ShipmentsTable',
    components: { DateCell, ShipmentDocumentLink },
    mixins: [languageMixin],
    emits: ['sort-change'],
    data() {
        return {
            preparedSortParams: '',
            sortBy: [],
            sortDesc: [],
            sortKeys: {
                created_at: 'created_at',
                status: 'status',
                senders: 'senders',
                created_by: 'created_by',
                recipient: 'recipient',
            },
        }
    },
    computed: {
        ...mapGetters('shipments', {
            getAllShipments: getterTypes.getAllShipments,
            isFetching: getterTypes.isFetching,
        }),
        headers: function () {
            return [
                {
                    text: this.$t('pages.shipments.created'),
                    value: 'created_at',
                    sortable: true,
                },
                {
                    text: this.$t('pages.shipments.usManager'),
                    value: 'senders',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.recipient'),
                    value: 'recipient',
                    align: 'center',
                    sortable: true,
                },
                {
                    text: this.$t('pages.shipments.employee'),
                    value: 'created_by',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.totalPrice'),
                    value: 'total_price',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.paid'),
                    value: 'paid',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.outstanding'),
                    value: 'outstanding',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.status'),
                    value: 'status',
                    align: 'center',
                    sortable: true,
                },
                {
                    text: this.$t('pages.shipments.document'),
                    value: 'document_link',
                    align: 'center',
                    sortable: false,
                },
            ]
        },
        filteredShipments: function () {
            return this.getAllShipments.shipments ?? []
        },
    },
    watch: {
        preparedSortParams: function () {
            this.emitSortChange()
        },
    },
    methods: {
        formatPrice: function (price, currency = 'ILS') {
            return formatPriceWithCurrency(price, currency)
        },
        getOutstanding: function (item) {
            return (item.sum || 0) - (item.paid_sum || 0)
        },
        formatSenders,
        getStatusColor: function (status) {
            if (!status) return 'grey'
            const statusLower = status.toLowerCase()
            if (statusLower === 'full') return 'green'
            if (statusLower === 'partial') return 'orange'
            if (statusLower === 'unpaid') return 'red'
            return 'grey'
        },
        getStatusText: function (status) {
            if (!status) return this.$t('pages.shipments.unknown')
            return this.$t(`pages.shipments.${status.toLowerCase()}`)
        },
        getRecipientName: function (item) {
            if (item.recipient_first_name || item.recipient_last_name) {
                const firstName = item.recipient_first_name || ''
                const lastName = item.recipient_last_name || ''
                return `${firstName} ${lastName}`.trim() || '-'
            }

            if (item.recipient && item.recipient.nickName) {
                return item.recipient.nickName
            }

            return '-'
        },
        openShipment: function (item) {
            const shipmentId = item.id || item.shipment_id
            if (shipmentId) {
                this.$router.push({ path: `/shipments/${shipmentId}` })
            }
        },
        updateSortBy: function (value) {
            const validSortBy = Array.isArray(value)
                ? value.filter((key) => this.sortKeys[key])
                : value && this.sortKeys[value]
                ? [value]
                : []

            this.sortBy = validSortBy
            if (Array.isArray(this.sortDesc) && this.sortDesc.length !== validSortBy.length) {
                this.sortDesc = validSortBy.map((_, index) =>
                    this.sortDesc[index] !== undefined ? this.sortDesc[index] : false
                )
            }

            const newSortParams = prepareSortOptionsReports(
                this.sortBy,
                this.sortDesc,
                this.sortKeys
            )

            if (arraysEqual(this.preparedSortParams, newSortParams)) {
                return
            }

            this.preparedSortParams = newSortParams
        },
        updateSortDesc: function (value) {
            this.sortDesc = value
            const validSortBy = Array.isArray(this.sortBy)
                ? this.sortBy.filter((key) => this.sortKeys[key])
                : this.sortBy && this.sortKeys[this.sortBy]
                ? [this.sortBy]
                : []

            this.sortBy = validSortBy
            if (Array.isArray(this.sortDesc) && this.sortDesc.length !== validSortBy.length) {
                this.sortDesc = validSortBy.map((_, index) =>
                    this.sortDesc[index] !== undefined ? this.sortDesc[index] : false
                )
            }

            const newSortParams = prepareSortOptionsReports(
                this.sortBy,
                this.sortDesc,
                this.sortKeys
            )

            if (arraysEqual(this.preparedSortParams, newSortParams)) {
                return
            }

            this.preparedSortParams = newSortParams
        },
        emitSortChange: function () {
            const sortValue =
                Array.isArray(this.preparedSortParams) &&
                this.preparedSortParams.length > 0
                    ? this.preparedSortParams[0]
                    : ''
            this.$emit('sort-change', sortValue)
        },
    },
}
</script>

<style lang="scss" scoped>
.shipments-table {
    &__table {
        width: 100%;
        position: relative;
        &:before {
            content: '';
            position: absolute;
            top: -4px;
            left: 0;
            width: 100%;
            height: 4px;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.04), transparent);
        }
    }
}

::v-deep .v-data-table-header th {
    white-space: nowrap !important;
}

::v-deep .v-data-table-header th .v-data-table-header__content {
    display: flex !important;
    align-items: center;
    gap: 6px;
}

::v-deep .v-data-table tbody tr {
    cursor: pointer;
}

::v-deep .v-data-table tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
}

.shipments-table {
    &__debt--unpaid {
        color: #d32f2f;
    }

    &__document-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        transition: opacity 0.2s;
        padding: 2px ;
        border-radius: 4px;
        max-height: 24px;
        max-width: 24px;

        &:hover {
            opacity: 0.7;
        }

        svg {
            display: block;
        }
    }

    &__document-icon {
        width: 18px;
        height: 18px;
        color: red;
    }
}
</style>

